/**
 * @description Pack SO page chrome — full-width column, title h1, optional sub-nav (LYN-363)
 *
 * Key features:
 * - Drops max-w-3xl mx-auto so the column fills beside the rail
 * - One h1: the page title (Dashboard, Company, Settings)
 * - Sub-nav under the title only when the page passes items
 *
 * @dependencies react-router-dom NavLink
 *
 * @notes Do not invent tabs. Pages with one view omit items. Sidebar stays
 *   PackSOSidebar. Not a LynxPay reskin (no AppHeader, brand-yellow tabs,
 *   or Untitled packages). A page may still center its own form.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {Array<{ key: string, label: string, to?: string, active?: boolean }>} [props.items]
 * @param {React.ReactNode} [props.children]
 */
export default function PackPage({ title, subtitle, items, children }) {
  const navItems = Array.isArray(items) ? items.filter((item) => item?.key && item?.label) : [];
  const hasSubNav = navItems.length > 0;

  return (
    <div className="w-full px-6 py-8" data-testid="pack-page">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
        {subtitle ? (
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        ) : null}
        {hasSubNav ? (
          <nav
            className="mt-4 border-b border-gray-200"
            aria-label="Page sections"
            data-testid="pack-page-subnav"
          >
            <div className="flex space-x-8">
              {navItems.map((item) => {
                const className = (isActive) =>
                  `py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`;

                if (item.to) {
                  return (
                    <NavLink
                      key={item.key}
                      to={item.to}
                      end
                      className={({ isActive }) => className(isActive)}
                    >
                      {item.label}
                    </NavLink>
                  );
                }

                return (
                  <span
                    key={item.key}
                    className={className(item.active === true)}
                  >
                    {item.label}
                  </span>
                );
              })}
            </div>
          </nav>
        ) : null}
      </header>
      {children}
    </div>
  );
}
